import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { motion } from "motion/react";
import { api, useAction, useApi } from "../api.ts";
import { useLiveEvent } from "../live.tsx";
import { fmtDateTime, fmtRelative } from "../format.ts";
import { Badge, Button, Card, Dialog, EmptyState, ErrorText, Field, Icon, Input, LoadError, Loading, PageHeader, Table, TBody, Td, Textarea, Th, THead, cn, useToast } from "../ui/index.tsx";
import { PurchaseOptions, TicketStatusBadge } from "./_shared.tsx";
import { EASE, Springy, Stage } from "./member/kit.tsx";
import type { Ticket, TicketSummary } from "../../shared/types.ts";

function NewTicketDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const create = useAction(async () => {
    const t = await api.post<Ticket>("/tickets", { subject: subject.trim(), message: message.trim() });
    toast("Ticket created", "success");
    setSubject("");
    setMessage("");
    onClose();
    navigate(`/support/${t.id}`);
  });

  const onSubmit = (e: FormEvent) => { e.preventDefault(); void create.run(); };
  const valid = subject.trim().length > 0 && message.trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} title="New ticket" description="Describe the problem and include any error code you see."
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={create.pending} disabled={!valid} onClick={() => void create.run()}>Create ticket</Button>
      </>}>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Subject"><Input autoFocus maxLength={200} value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
        <Field label="Message"><Textarea rows={6} maxLength={5000} value={message} onChange={(e) => setMessage(e.target.value)} /></Field>
        <ErrorText>{create.error}</ErrorText>
      </form>
    </Dialog>
  );
}

export default function Support() {
  const tickets = useApi<TicketSummary[]>("/tickets");
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [buying, setBuying] = useState(false);

  useLiveEvent(["ticket"], () => { void tickets.reload(); });

  const list = tickets.data ?? [];

  return (
    <Stage>
      <PageHeader title="Support" description="Open a ticket and we'll get back to you."
        actions={<>
          <Springy><Button icon="card" onClick={() => setBuying(true)}>Request purchase</Button></Springy>
          <Springy><Button variant="primary" icon="plus" onClick={() => setCreating(true)}>New ticket</Button></Springy>
        </>} />

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1, ease: EASE }}>
        <Card padded={false}>
          {tickets.loading && !tickets.data ? <Loading /> : tickets.error && !tickets.data ? <LoadError error={tickets.error} retry={tickets.reload} /> :
            list.length === 0 ? (
              <EmptyState icon="lifebuoy" title="No tickets yet" body="Having trouble, or want to buy a license? Open a ticket."
                action={<Springy><Button variant="primary" icon="plus" onClick={() => setCreating(true)}>New ticket</Button></Springy>} />
            ) : (
              <Table>
                <THead><tr><Th>#</Th><Th>Subject</Th><Th>Status</Th><Th>Updated</Th></tr></THead>
                <TBody>
                  {list.map((t, i) => (
                    <motion.tr key={t.id} onClick={() => navigate(`/support/${t.id}`)} className="cursor-pointer transition-colors hover:bg-hover/60"
                      initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: 0.15 + Math.min(i, 10) * 0.045, ease: EASE }}>
                      <Td className="text-faint tabular-nums">{t.id}</Td>
                      <Td className="max-w-xs truncate font-medium"><Link to={`/support/${t.id}`} className="hover:underline">{t.subject}</Link></Td>
                      <Td><TicketStatusBadge status={t.status} /></Td>
                      <Td className="whitespace-nowrap text-muted">{fmtRelative(t.updated_at)}</Td>
                    </motion.tr>
                  ))}
                </TBody>
              </Table>
            )}
        </Card>
      </motion.div>

      <NewTicketDialog open={creating} onClose={() => setCreating(false)} />
      <Dialog open={buying} onClose={() => setBuying(false)} wide title="Request a purchase" description="Choose a game and duration. We'll open a ticket to complete it.">
        <PurchaseOptions />
      </Dialog>
    </Stage>
  );
}

export function TicketView() {
  const { id = "" } = useParams();
  const ticket = useApi<Ticket>(`/tickets/${encodeURIComponent(id)}`);
  const { setData, reload } = ticket;
  const [reply, setReply] = useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const t = ticket.data;

  useLiveEvent(["ticket"], (m) => { if (m.type === "ticket" && String(m.ticket_id) === id) void reload(); });
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [t?.messages.length]);

  const send = useAction(async () => {
    const updated = await api.post<Ticket>(`/tickets/${encodeURIComponent(id)}/messages`, { message: reply.trim() });
    setData(updated);
    setReply("");
  });

  const closed = t?.status === "closed";
  const onSubmit = (e: FormEvent) => { e.preventDefault(); if (reply.trim()) void send.run(); };

  return (
    <Stage>
      <div className="mx-auto max-w-5xl">
        <Link to="/support" className="group mb-6 inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-fg">
          <Icon name="chevronLeft" className="size-4 transition-transform group-hover:-translate-x-1" />All tickets
        </Link>
        {ticket.loading && !t ? <Loading /> : ticket.error && !t ? (
          ticket.error.status === 404
            ? <EmptyState icon="ticket" title="Ticket not found" />
            : <LoadError error={ticket.error} retry={reload} />
        ) : t && (
          <>
            <motion.div className="mb-6 flex flex-wrap items-start justify-between gap-3" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: EASE }}>
              <div className="min-w-0">
                <h1 className="break-words text-2xl font-semibold tracking-tight">{t.subject}</h1>
                <p className="mt-1 text-xs text-faint">Ticket #{t.id} · opened {fmtDateTime(t.created_at)}</p>
              </div>
              <motion.div key={t.status} initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 420, damping: 16 }}>
                <TicketStatusBadge status={t.status} />
              </motion.div>
            </motion.div>

            <ol className="space-y-4">
              {t.messages.map((m, i) => (
                <motion.li key={m.id} className={cn("flex", m.is_admin ? "justify-start" : "justify-end")}
                  initial={{ opacity: 0, x: m.is_admin ? -32 : 32, scale: 0.96 }} animate={{ opacity: 1, x: 0, scale: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 26, delay: Math.min(i, 8) * 0.06 }}>
                  <div className={cn("max-w-[85%] rounded-xl border px-4 py-3",
                    m.is_admin ? "border-accent/25 bg-accent-soft" : "border-line bg-surface")}>
                    <div className="mb-1 flex items-center gap-2 text-xs">
                      {m.is_admin ? <Badge tone="accent">Support</Badge> : <span className="font-medium text-muted">You</span>}
                      <span className="text-faint">{fmtDateTime(m.created_at)}</span>
                    </div>
                    <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{m.message}</p>
                  </div>
                </motion.li>
              ))}
            </ol>
            <div ref={endRef} />

            <form onSubmit={onSubmit} className="mt-6 space-y-3 border-t border-line pt-6">
              {closed ? (
                <p className="rounded-lg border border-line bg-surface px-4 py-3 text-sm text-muted">This ticket is closed. Open a new ticket if you still need help.</p>
              ) : (
                <>
                  <Textarea rows={4} placeholder="Write a reply…" maxLength={5000} value={reply} onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && reply.trim()) void send.run(); }} />
                  <ErrorText>{send.error}</ErrorText>
                  <div className="flex justify-end">
                    <Springy><Button type="submit" variant="primary" icon="send" loading={send.pending} disabled={!reply.trim()}>Send reply</Button></Springy>
                  </div>
                </>
              )}
            </form>
          </>
        )}
      </div>
    </Stage>
  );
}
